import React, { useState, useEffect, useCallback } from "react";
import OfflineServices from "services/OfflineServices";
import AdminServices from "services/AdminServices";
import PageTitle from "components/Typography/PageTitle";
import TableLoading from "components/preloader/TableLoading";
import Somethingwentwrong from "pages/Somethingwentwrong";
import {
  Card,
  CardBody,
  Input,
  Select,
  Table,
  TableCell,
  TableContainer,
  TableHeader,
} from "@windmill/react-ui";
import sidebar from "routes/sidebar";
import NotFound from "components/table/NotFound";
import OfflineTable, { OFFLINE_STATUS } from "components/languageDriver/OfflineTable";
import OfflineQueryDrawer from "components/drawer/OfflineQueryDrawer";

// Offline queries work like tickets: the Admin sees every query and assigns
// or rejects it; an employee sees only the queries assigned to them and
// resolves them with an answer (the server enforces the same rules).
const Offline = () => {
  const adminInfo = localStorage.getItem("adminInfo")
    ? JSON.parse(localStorage.getItem("adminInfo"))
    : null;
  const isAdmin =
    adminInfo?.id === 1 || String(adminInfo?.profile || "").toLowerCase() === "admin";
  const hideView = isAdmin
    ? true
    : adminInfo?.permission?.some((perm) => perm.id_tab === "offlinequery" && perm.view === 1);

  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [selected, setSelected] = useState(null);

  // Refreshed every 15 seconds so new / newly assigned queries show up.
  const loadQueries = useCallback(() => {
    OfflineServices.getAllOfflineQuery()
      .then((res) => setData(res))
      .catch(() => setData({ status: false }))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => {
    if (!hideView) return undefined;
    loadQueries();
    const timer = setInterval(loadQueries, 15000);
    return () => clearInterval(timer);
  }, [hideView, loadQueries]);

  // Employees an Admin can assign to (active, not admins).
  useEffect(() => {
    if (!isAdmin) return;
    AdminServices.getAllStaff()
      .then((res) => {
        const rows = Array.isArray(res?.data) ? res.data : [];
        setEmployees(
          rows.filter(
            (e) => String(e.status || "Active") === "Active" && String(e.role || "").toLowerCase() !== "admin"
          )
        );
      })
      .catch(() => setEmployees([]));
  }, [isAdmin]);

  const status = data?.status;
  const rows = Array.isArray(data?.data) ? data.data : [];
  const filtered = rows.filter(
    (item) =>
      (!statusFilter || item.status === statusFilter) &&
      (!searchText ||
        `${item.ref_no || ""} ${item.subject || ""} ${item.company_name || ""} ${item.content || ""}`
          .toLowerCase()
          .includes(searchText.toLowerCase()))
  );
  const counts = rows.reduce((acc, r) => ({ ...acc, [r.status]: (acc[r.status] || 0) + 1 }), {});

  return (
    <>
      {hideView && (
        <>
          <PageTitle sidebar={sidebar().sidebar} />
          <OfflineQueryDrawer
            query={selected}
            isAdmin={isAdmin}
            employees={employees}
            currentUserId={adminInfo?.id}
            onClose={() => setSelected(null)}
            onChanged={loadQueries}
          />

          <Card className="min-w-0 shadow-xs overflow-hidden bg-white dark:bg-gray-800 mb-5">
            <CardBody>
              <div className="flex gap-4 items-center flex-wrap">
                <div className="flex-1 min-w-0">
                  <Input
                    className="border h-10 text-sm focus:outline-none block w-full bg-gray-100 border-transparent focus:bg-white"
                    type="search"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    placeholder={isAdmin ? "Search ticket no., subject, agent or message" : "Search your assigned queries"}
                  />
                </div>
                <Select className="h-10 text-sm w-48" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                  <option value="">All ({rows.length})</option>
                  {Object.entries(OFFLINE_STATUS).map(([key, s]) => (
                    <option key={key} value={key}>
                      {s.label} ({counts[key] || 0})
                    </option>
                  ))}
                </Select>
              </div>
            </CardBody>
          </Card>

          <div className="data-table-container">
            {loading ? (
              <TableLoading row={12} col={6} width={190} height={20} />
            ) : status === false || status === undefined ? (
              <Somethingwentwrong title="Something went wrong, please try again later." />
            ) : filtered.length > 0 ? (
              <TableContainer className="mb-8" style={{ maxHeight: "65vh", overflowY: "scroll", scrollbarWidth: "thin" }}>
                <Table>
                  <TableHeader className="custom-table-header capitalize sticky top-0 z-10">
                    <tr>
                      <TableCell className="text-center">Sl.No.</TableCell>
                      <TableCell className="text-center">Ticket</TableCell>
                      <TableCell className="text-center">Agent</TableCell>
                      <TableCell className="text-center">Subject</TableCell>
                      <TableCell className="text-center">Received</TableCell>
                      <TableCell className="text-center">Status</TableCell>
                      <TableCell className="text-center">Assigned to</TableCell>
                      <TableCell className="text-center">Actions</TableCell>
                    </tr>
                  </TableHeader>
                  <OfflineTable roledata={filtered} onOpen={setSelected} />
                </Table>
              </TableContainer>
            ) : (
              <NotFound
                title={
                  isAdmin
                    ? "There are no offline queries."
                    : "No offline queries are assigned to you."
                }
              />
            )}
          </div>
        </>
      )}
    </>
  );
};

export default Offline;
